import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import * as React from "react";

import SelectBooleanValue from "components/custom-filters-list/filters/select-boolean-value.component";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import SelectTag from "components/custom-filters-list/filters/select-tag.component";
import Select from "components/custom-filters-list/filters/select.component";
import useCustomFilters, {
  FilterOption
} from "components/custom-filters-list/filters/use-custom-filters.hook";
import { NON_BREAKABLE_SPACE } from "constants/common";
import { TEAMS_FEATURE } from "constants/feature-flags";
import { TASK_PRIORITIES, TASK_STATUSES } from "constants/staff-tasks";
import { TagTypes } from "constants/tags";
import { useGetTeamsQuery } from "services/teams";
import { StaffTasksFilter } from "services/types/staff-tasks.type";

export type PatientTasksCustomFiltersValues = {
  assignee?: { label: string; value: string }[] | null;
  created_by?: { label: string; value: string } | null;
  status?: { label: string; value: string }[] | null;
  priority?: { label: string; value: string }[] | null;
  created_from_bulk_action?: { label: string; value: boolean } | null;
  assigned_tags?: { label: string; value: string }[] | null;
  /** Teams (TEAMS_FEATURE): the team a task was sent to. */
  team?: { label: string; value: string } | null;
  group_by_priority: false;
};

type UseTasksCustomFiltersProps = {
  updateCustomFilter: (
    filterName: keyof StaffTasksFilter,
    filterValue: StaffTasksFilter[keyof StaffTasksFilter] | undefined | null
  ) => void;
  selectedCustomFilters: PatientTasksCustomFiltersValues;
  setSelectedCustomFilters: React.Dispatch<
    React.SetStateAction<PatientTasksCustomFiltersValues>
  >;
};

const TASK_STATUS_OPTIONS = TASK_STATUSES.getOptions();
const TASK_PRIORITY_OPTIONS = TASK_PRIORITIES.getOptions();

export default function useTasksV2CustomFilters({
  updateCustomFilter,
  selectedCustomFilters,
  setSelectedCustomFilters
}: UseTasksCustomFiltersProps) {
  const enableTeams = Boolean(useFlags()[TEAMS_FEATURE]);
  const { data: teamsData } = useGetTeamsQuery(
    { is_active: true },
    { skip: !enableTeams }
  );
  const teamOptions = useMemo(
    () =>
      (teamsData?.results ?? []).map((team) => ({
        label: team.name,
        value: String(team.id)
      })),
    [teamsData]
  );

  const filterOptions: FilterOption<
    PatientTasksCustomFiltersValues,
    StaffTasksFilter
  >[] = useMemo(() => {
    const teamFilter: FilterOption<
      PatientTasksCustomFiltersValues,
      StaffTasksFilter
    >[] = enableTeams
      ? [
          {
            name: "team",
            label: "Team",
            filterKey: "7",
            renderViewMode: (_filterKey, metadata) => (
              <span>
                Team: <b>{metadata?.label}</b>
              </span>
            ),
            editComponent: (_filterKey, setMetadata, control, updateFilter) => (
              <Select
                name="team"
                label="Team"
                options={teamOptions}
                control={control}
                enablePortal
                onOptionSelected={(option) => {
                  updateFilter(option?.value ?? null);
                  setMetadata(option);
                }}
              />
            )
          }
        ]
      : [];
    return [
      {
        name: "assignee",
        label: "Assignee",
        filterKey: "1",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Assignee:{" "}
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectProvider
            name="assignee"
            ignoreRegionsFilter
            ignoreClinicsFilter
            control={control}
            label="By Assignee"
            autoFocus
            enablePortal
            multiple
            openOnFocus
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
          />
        )
      },
      {
        name: "created_by",
        label: `Created${NON_BREAKABLE_SPACE}By`,
        filterKey: "2",
        renderViewMode: (filterKey, metadata) => (
          <span>
            Created By: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectProvider
            name="created_by"
            ignoreRegionsFilter
            ignoreClinicsFilter
            control={control}
            label="Created By"
            enablePortal
            onOptionSelected={(option) => {
              updateFilter(option?.value);
              setMetadata(option);
            }}
          />
        )
      },
      {
        name: "status",
        label: "Status",
        filterKey: "3",
        renderViewMode: (filterKey, metadata) => (
          <span>
            Status:{" "}
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <Select
            name="status"
            label="Status"
            options={TASK_STATUS_OPTIONS}
            control={control}
            enablePortal
            multiple
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");

                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }
              setMetadata(selected);
            }}
          />
        )
      },
      {
        name: "priority",
        label: "Priority",
        filterKey: "4",
        renderViewMode: (filterKey, metadata) => (
          <span>
            Priority:{" "}
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <Select
            name="priority"
            label="Priority"
            options={TASK_PRIORITY_OPTIONS}
            control={control}
            enablePortal
            multiple
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");

                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }
              setMetadata(selected);
            }}
          />
        )
      },
      {
        name: "created_from_bulk_action",
        label: "Bulk Tasks",
        filterKey: "5",
        renderViewMode: (filterKey, metadata) => (
          <span>
            Bulk Tasks: <b>{metadata?.value ? "Yes" : "No"}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectBooleanValue
            name="created_from_bulk_action"
            label="Bulk Tasks"
            control={control}
            enablePortal
            onOptionSelected={(selected) => {
              updateFilter(selected?.value);
              setMetadata(selected);
            }}
          />
        )
      },
      ...teamFilter,
      {
        name: "assigned_tags",
        label: "Tags",
        filterKey: "6",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Tags:{" "}
            <b>
              {metadata?.length === 1
                ? `${metadata[0].label}`
                : `${metadata?.length ?? 0} selected`}
            </b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectTag
            name="assigned_tags"
            tagType={TagTypes.TASK}
            label="By Tags"
            control={control}
            enablePortal
            onOptionSelected={(option) => {
              updateFilter(
                Array.isArray(option)
                  ? option
                      ?.map((item: { value: string }) => item?.value)
                      .join(",")
                  : option?.value
              );
              setMetadata(option);
            }}
          />
        )
      }
    ];
  }, [enableTeams, teamOptions]);

  const filters = useCustomFilters<
    PatientTasksCustomFiltersValues,
    StaffTasksFilter
  >({
    updateCustomFilter,
    filterOptions,
    selectedCustomFilters,
    setSelectedCustomFilters
  });

  return filters;
}
